// The freeform canvas: renders each widget absolutely from its grid position,
// scales the whole content by the zoom, and hand-rolls move + resize with pointer
// events (no react-grid-layout — it can't express freeform + drag-to-merge).
// The store is the source of truth; a drag shows a live pixel preview and commits
// a snapped grid position on release.

import { useState } from "react";
import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { clampFreeform, type GridPosition } from "@catbento/shared";
import type { WidgetInstance, PlayerRef } from "../types.js";
import { getWidgetDef } from "../registry.js";
import { STEP, pxSpan, unitsToPx, snapToCell, snapToSize, bestOverlapTarget } from "../geometry.js";
import { mergePlayers } from "../dropResolution.js";
import WidgetShell from "./WidgetShell.js";
import { theme } from "../theme.js";

interface PreviewPx {
  instanceId: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Props {
  instances: WidgetInstance[];
  scale: number;
  /** Ref to the SCALED content div — V2App uses it to map a palette drop to a cell. */
  contentRef: RefObject<HTMLDivElement | null>;
  onMove: (instanceId: string, position: GridPosition) => void;
  onResize: (instanceId: string, position: GridPosition) => void;
  onRemove: (instanceId: string) => void;
  onPickPlayer: (instanceId: string, ref: PlayerRef) => void;
  onPlayerDragStart: (e: ReactPointerEvent, ref: PlayerRef) => void;
  /** Drag a whole single-player card onto a mergeable card (merge) or a
   *  replace-on-drop card like Matches (swap its player). */
  onCardDrop: (sourceId: string, targetId: string) => void;
  /** The widget a dragged player TOKEN (from V2App) is over, and the drop tone:
   *  "merge" (blue) / "ok" (green) / "bad" (red). null = nothing hovering. */
  dropTarget: { instanceId: string; state: "merge" | "ok" | "bad" } | null;
  /** The widget that just absorbed a merge — plays a one-shot blue pop/flash. */
  flashId: string | null;
}

export default function Canvas({
  instances,
  scale,
  contentRef,
  onMove,
  onResize,
  onRemove,
  onPickPlayer,
  onPlayerDragStart,
  onCardDrop,
  dropTarget,
  flashId,
}: Props) {
  const [preview, setPreview] = useState<PreviewPx | null>(null);
  // While dragging a whole single-player card, the card it's over and the drop
  // tone: "merge" = will combine into a comparison (blue), "ok" = a valid replace
  // (green), "bad" = a no-op duplicate/full (red).
  const [mergeTarget, setMergeTarget] = useState<{ id: string; state: "merge" | "ok" | "bad" } | null>(null);

  function beginDrag(e: ReactPointerEvent, instanceId: string, kind: "move" | "resize") {
    const inst = instances.find((i) => i.instanceId === instanceId);
    if (!inst) return;
    e.preventDefault();
    const origPx = unitsToPx(inst.position);
    const origPos = inst.position;
    const startX = e.clientX;
    const startY = e.clientY;
    let latest: PreviewPx = { instanceId, ...origPx };
    setPreview(latest);

    // Only a single-player card can be dragged as a drop source (a multi-player
    // card has no single player to carry — its columns are dragged individually).
    const sourceSingle = kind === "move" && inst.players.length === 1 && Boolean(inst.players[0]);
    const sourcePlayer = inst.players[0] ?? null;
    // OTHER cards this drag could drop INTO: mergeable (add a column) OR
    // replace-on-drop (swap its player, e.g. Matches).
    const dropCandidates = sourceSingle
      ? instances
          .filter((i) => {
            const d = getWidgetDef(i.widgetKey);
            return i.instanceId !== instanceId && (d?.mergeable || d?.replaceOnDrop);
          })
          .map((i) => ({ instanceId: i.instanceId, position: i.position }))
      : [];
    let mt: { id: string; state: "merge" | "ok" | "bad" } | null = null;

    const onPointerMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      if (kind === "move") {
        latest = { instanceId, left: origPx.left + dx, top: origPx.top + dy, width: origPx.width, height: origPx.height };
        // The card it now covers most (snapped to grid) is the drop target. Green
        // if the drop does something: a merge that adds a player, or a replace.
        // Red if it's a no-op (duplicate/full merge).
        const overId = dropCandidates.length
          ? bestOverlapTarget(
              { x: snapToCell(latest.left), y: snapToCell(latest.top), w: origPos.w, h: origPos.h },
              dropCandidates
            )
          : null;
        if (overId && sourcePlayer) {
          const target = instances.find((i) => i.instanceId === overId);
          const tDef = target ? getWidgetDef(target.widgetKey) : null;
          // Merge (add a comparison column) = blue; replace (e.g. Matches) = green;
          // a duplicate/full merge = red (a no-op).
          const state: "merge" | "ok" | "bad" = tDef?.mergeable
            ? mergePlayers(target?.players ?? [], sourcePlayer, tDef.maxPlayers).added
              ? "merge"
              : "bad"
            : tDef?.replaceOnDrop
              ? "ok"
              : "bad";
          mt = { id: overId, state };
        } else {
          mt = null;
        }
        setMergeTarget(mt);
      } else {
        latest = {
          instanceId,
          left: origPx.left,
          top: origPx.top,
          width: Math.max(pxSpan(1), origPx.width + dx),
          height: Math.max(pxSpan(1), origPx.height + dy),
        };
      }
      setPreview(latest);
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      setPreview(null);
      setMergeTarget(null);
      if (kind === "move") {
        // Over a drop target: blue merge / green replace → commit (copy the player
        // in; source snaps back); red → no-op (duplicate/full merge) → snap back.
        if (mt) {
          if (mt.state !== "bad") onCardDrop(instanceId, mt.id);
          return;
        }
        const pos = clampFreeform({ x: snapToCell(latest.left), y: snapToCell(latest.top), w: origPos.w, h: origPos.h });
        if (pos.x !== origPos.x || pos.y !== origPos.y) onMove(instanceId, pos);
      } else {
        const pos = clampFreeform({ x: origPos.x, y: origPos.y, w: snapToSize(latest.width), h: snapToSize(latest.height) });
        if (pos.w !== origPos.w || pos.h !== origPos.h) onResize(instanceId, pos);
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  // Size the content so it scrolls to fit everything, with headroom to drag into.
  let maxRight = 1200;
  let maxBottom = 720;
  for (const inst of instances) {
    const px = unitsToPx(inst.position);
    maxRight = Math.max(maxRight, px.left + px.width + 400);
    maxBottom = Math.max(maxBottom, px.top + px.height + 400);
  }

  return (
    <div style={{ flex: 1, overflow: "auto", background: theme.page, position: "relative" }}>
      {/* Empty-state hint, centered in the visible canvas (not the scaled content,
          so it stays put at any zoom). pointer-events: none so a palette drop onto
          the empty canvas still lands. */}
      {instances.length === 0 ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
            color: theme.inkFaint,
            fontSize: 14,
          }}
        >
          Drag a widget from the left onto the canvas to begin.
        </div>
      ) : null}
      <div
        ref={contentRef}
        style={{
          position: "relative",
          width: maxRight,
          height: maxBottom,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          // The prototype's workspace grid: solid #1f1f1f 1px lines on a 110px cell
          // (STEP), aligned to the widget cells (a line at each cell's left/top edge).
          backgroundImage:
            `linear-gradient(${theme.grid} 1px, transparent 1px),` +
            ` linear-gradient(90deg, ${theme.grid} 1px, transparent 1px)`,
          backgroundSize: `${STEP}px ${STEP}px`,
        }}
      >
        {instances.map((inst) => {
          const def = getWidgetDef(inst.widgetKey);
          if (!def) return null; // unknown key (e.g. a widget not built yet) — skip
          const px =
            preview && preview.instanceId === inst.instanceId ? preview : unitsToPx(inst.position);
          const dragging = preview?.instanceId === inst.instanceId;
          return (
            <div
              key={inst.instanceId}
              style={{
                position: "absolute",
                left: px.left,
                top: px.top,
                width: px.width,
                height: px.height,
                zIndex: dragging ? 10 : 1,
                opacity: dragging ? 0.85 : 1,
              }}
            >
              <WidgetShell
                instance={inst}
                def={def}
                flash={flashId === inst.instanceId}
                onMoveStart={(e) => beginDrag(e, inst.instanceId, "move")}
                onResizeStart={(e) => beginDrag(e, inst.instanceId, "resize")}
                onRemove={onRemove}
                onPickPlayer={onPickPlayer}
                onPlayerDragStart={onPlayerDragStart}
                dropState={
                  dropTarget?.instanceId === inst.instanceId
                    ? dropTarget.state
                    : mergeTarget?.id === inst.instanceId
                      ? mergeTarget.state
                      : null
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
